import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Avatar from '../components/ui/Avatar';
import Button from '../components/ui/Button';
import { formatPrice, formatNumber } from '../utils/formatters';

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { users, currentUser } = useApp();
  const [activeCategory, setActiveCategory] = useState('volume');
  const [timePeriod, setTimePeriod] = useState('monthly');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'volume', label: 'Trading Volume', icon: '💰', metric: 'totalVolume' },
    { id: 'profit', label: 'Total Profit', icon: '📈', metric: 'roi' },
    { id: 'sales', label: 'Most Sales', icon: '🛒', metric: 'totalSales' },
    { id: 'speed', label: 'Speed Demon', icon: '⚡', metric: 'avgFlipTime', reverse: true },
    { id: 'roi', label: 'Best ROI', icon: '🎯', metric: 'roi' },
  ];

  const activeConfig = categories.find(c => c.id === activeCategory);

  // Sort users by active category metric
  const rankedUsers = [...users]
    .filter(u => u.stats[activeConfig.metric] != null)
    .sort((a, b) => {
      const aVal = a.stats[activeConfig.metric];
      const bVal = b.stats[activeConfig.metric];
      return activeConfig.reverse ? aVal - bVal : bVal - aVal;
    })
    .filter(u =>
      searchQuery
        ? u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
          u.displayName.toLowerCase().includes(searchQuery.toLowerCase())
        : true
    )
    .map((user, index) => ({ ...user, rank: index + 1 }));

  const top3 = rankedUsers.slice(0, 3);
  const rest = rankedUsers.slice(3);

  const currentUserRank = rankedUsers.find(u => u.username === currentUser.username);

  const formatMetricValue = (value, metric) => {
    if (metric === 'totalVolume') return formatPrice(value);
    if (metric === 'avgFlipTime') return `${value}d`;
    if (metric === 'roi') return `+${value}%`;
    return formatNumber(value);
  };

  const PodiumCard = ({ user, position }) => {
    const heights = { 1: 'h-64', 2: 'h-48', 3: 'h-40' };
    const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };
    const gradients = {
      1: 'from-yellow-100 to-yellow-200',
      2: 'from-gray-100 to-gray-200',
      3: 'from-orange-100 to-orange-200',
    };

    return (
      <div className="flex flex-col items-center">
        <Avatar src={user.avatar} size="2xl" className="mb-4" />
        <p className="font-bold text-lg mb-1">{user.displayName}</p>
        <p className="text-sm text-gray-600 mb-2">@{user.username}</p>
        <div className="flex gap-1 mb-4">
          {user.badges?.slice(0, 2).map(badge => (
            <Badge key={badge} size="sm" variant="success">
              {badge === 'topTrader' ? '🏆' : badge === 'eliteMember' ? '💎' : '⚡'}
            </Badge>
          ))}
        </div>
        <Card
          className={`${heights[position]} w-40 bg-gradient-to-b ${gradients[position]} flex flex-col items-center justify-center cursor-pointer hover:scale-105 transition-transform`}
          onClick={() => navigate(`/profile/${user.username}`)}
        >
          <div className="text-6xl mb-4">{medals[position]}</div>
          <p className="text-3xl font-bold">
            {formatMetricValue(user.stats[activeConfig.metric], activeConfig.metric)}
          </p>
          <p className="text-sm text-gray-600 mt-2">#{position}</p>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4">🏆 Leaderboard</h1>
          <p className="text-xl text-gray-600">
            Compete with the best domain traders in the world
          </p>
        </div>

        {/* Category Tabs */}
        <Card className="mb-8">
          <div className="flex overflow-x-auto">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-4 font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeCategory === category.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="mr-2">{category.icon}</span>
                {category.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Time Period Filter */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex gap-2">
            {['daily', 'weekly', 'monthly', 'allTime'].map(period => (
              <Button
                key={period}
                variant={timePeriod === period ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setTimePeriod(period)}
              >
                {period.charAt(0).toUpperCase() + period.slice(1).replace('allTime', 'All Time')}
              </Button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Search traders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-4 py-2 border rounded-lg w-64"
          />
        </div>

        {/* Podium - Top 3 */}
        {top3.length >= 3 && (
          <div className="mb-16">
            <div className="flex items-end justify-center gap-8">
              {/* 2nd Place */}
              <PodiumCard user={top3[1]} position={2} />
              {/* 1st Place */}
              <PodiumCard user={top3[0]} position={1} />
              {/* 3rd Place */}
              <PodiumCard user={top3[2]} position={3} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Rankings Table */}
          <div className="lg:col-span-3">
            <Card>
              <div className="p-6 border-b">
                <h2 className="text-2xl font-bold">Rankings</h2>
              </div>
              <div className="divide-y">
                {rest.map((user) => (
                  <div
                    key={user.username}
                    className={`p-6 flex items-center gap-6 hover:bg-gray-50 cursor-pointer transition-colors ${
                      user.username === currentUser.username
                        ? 'bg-blue-50 hover:bg-blue-100'
                        : ''
                    }`}
                    onClick={() => navigate(`/profile/${user.username}`)}
                  >
                    {/* Rank */}
                    <div className="w-12 text-center">
                      <p className="text-2xl font-bold text-gray-600">#{user.rank}</p>
                    </div>

                    {/* Avatar */}
                    <Avatar src={user.avatar} size="lg" />

                    {/* User Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-lg">{user.displayName}</p>
                        {user.username === currentUser.username && (
                          <Badge variant="info" size="sm">You</Badge>
                        )}
                        {user.badges?.slice(0, 2).map(badge => (
                          <Badge key={badge} size="sm" variant="success">
                            {badge === 'topTrader' ? '🏆' : badge === 'eliteMember' ? '💎' : '⚡'}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-sm text-gray-600">@{user.username}</p>
                    </div>

                    {/* Stats */}
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">
                        {formatMetricValue(
                          user.stats[activeConfig.metric],
                          activeConfig.metric
                        )}
                      </p>
                      <p className="text-sm text-gray-600">{activeConfig.label}</p>
                    </div>

                    {/* Change indicator */}
                    <div className="w-16 text-center">
                      <span className="text-green-600 text-lg">↗ +5</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Your Rank */}
            {currentUserRank && (
              <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50">
                <h3 className="font-bold mb-4">Your Rank</h3>
                <div className="text-center mb-4">
                  <p className="text-5xl font-bold text-blue-600 mb-2">
                    #{currentUserRank.rank}
                  </p>
                  <p className="text-sm text-gray-600">{activeConfig.label}</p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Your Score:</span>
                    <span className="font-bold">
                      {formatMetricValue(
                        currentUserRank.stats[activeConfig.metric],
                        activeConfig.metric
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Top Rank:</span>
                    <span className="font-bold">
                      {formatMetricValue(
                        top3[0].stats[activeConfig.metric],
                        activeConfig.metric
                      )}
                    </span>
                  </div>
                </div>
                <Button fullWidth className="mt-4" size="sm">
                  View Profile
                </Button>
              </Card>
            )}

            {/* Monthly Prizes */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">🏅 Monthly Prizes</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🥇</span>
                    <span className="font-bold">1st Place</span>
                  </div>
                  <span className="font-bold text-yellow-600">$500</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🥈</span>
                    <span className="font-bold">2nd Place</span>
                  </div>
                  <span className="font-bold text-gray-600">$250</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🥉</span>
                    <span className="font-bold">3rd Place</span>
                  </div>
                  <span className="font-bold text-orange-600">$100</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4 text-center">
                Winners announced on the 1st of each month
              </p>
            </Card>

            {/* Competition Stats */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">📊 Competition Stats</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Traders:</span>
                  <span className="font-bold">{formatNumber(users.length)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Active This Month:</span>
                  <span className="font-bold">{formatNumber(users.length * 0.7)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Volume:</span>
                  <span className="font-bold">{formatPrice(5200000)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Avg Growth:</span>
                  <span className="font-bold text-green-600">+23%</span>
                </div>
              </div>
            </Card>

            {/* All Categories Ranks */}
            <Card className="p-6">
              <h3 className="font-bold mb-4">All Your Ranks</h3>
              <div className="space-y-2 text-sm">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className="w-full flex justify-between items-center p-2 hover:bg-gray-50 rounded transition-colors"
                  >
                    <span>
                      {category.icon} {category.label}
                    </span>
                    <Badge size="sm">#{Math.floor(Math.random() * 50) + 1}</Badge>
                  </button>
                ))}
              </div>
            </Card>

            {/* Share Button */}
            <Button fullWidth variant="outline">
              🔗 Share My Rank
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
